import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { loanFlowService, justificatifService } from '../../services/api';
import { X, Clock, User, MapPin, FileText, RefreshCw, Briefcase, Euro } from 'lucide-react';

const ShareData = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fiscalData, setFiscalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(240); // 4:00

  useEffect(() => {
    loadData();
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const loadData = async () => {
    try {
      const flow = await loanFlowService.getByClient(user.id);
      if (flow && flow.fiscalData) {
        setFiscalData(flow.fiscalData);
      } else {
        // Default data if not available
        setFiscalData({
          nom: user?.lastName || 'PRUVOST',
          prenom: user?.firstName || 'Jacky',
          dateNaissance: '15/03/1980',
          adresse: user?.address || '2 RUE DU 8 MAI',
          codePostal: user?.zipCode || '80130',
          ville: user?.city || 'MONTREUIL',
          revenuFiscal: '35000',
          nombreParts: '2'
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleShare = async () => {
    setLoading(true);
    try {
      let flow = await loanFlowService.getByClient(user.id);
      
      // If no flow exists, create one
      if (!flow) {
        flow = await loanFlowService.create({
          clientId: user.id,
          currentStep: 'avis_imposition_completed'
        });
      }
      
      if (flow) {
        await loanFlowService.update(flow.id, {
          currentStep: 'avis_imposition_completed',
          dataShared: true,
          sharedAt: new Date().toISOString()
        });
      }
      
      // Créer le justificatif validé pour avis d'imposition
      await justificatifService.createValidated(user.id, 'avis_imposition');
      
      // Redirect to success page for avis d'imposition
      navigate('/client/justificatif-success', { 
        state: { 
          type: 'avis_imposition',
          title: 'Avis d\'imposition'
        } 
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    navigate('/client/loan-flow/impots-login');
  };

  const handleClose = () => {
    navigate('/client/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      {/* MiTrust Window */}
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#1e3a5f] p-4">
          <div className="flex items-center justify-between">
            {/* Logos */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <img 
                  src="https://www.mitrust.io/assets/images/logo-white.svg" 
                  alt="MiTrust" 
                  className="h-6"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <span className="hidden text-white font-bold text-lg">MiTrust</span>
              </div>
              <span className="text-white/50">|</span>
              <div className="flex items-center">
                <span className="text-white font-bold text-lg tracking-wide">YOUNITED</span>
              </div>
            </div>
            {/* Timer and Close */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 text-white/80 text-sm">
                <Clock className="h-4 w-4" />
                <span>{formatTime(timeLeft)}</span>
              </div>
              <button 
                onClick={handleClose}
                className="text-white/80 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <div className="flex-1 py-3 text-center bg-gray-50">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold">✓</div>
              <span className="text-sm font-medium text-gray-500">Connexion</span>
            </div>
          </div>
          <div className="flex-1 py-3 text-center bg-white">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 rounded-full bg-[#1e3a5f] text-white text-xs flex items-center justify-center font-bold">2</div>
              <span className="text-sm font-medium text-gray-800">Partage</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 text-center mb-6">
            Partagez vos informations
          </h2>

          {/* User Card */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                {fiscalData?.prenom?.charAt(0)}{fiscalData?.nom?.charAt(0)}
              </div>
              
              {/* Info */}
              <div className="flex-1 space-y-2">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <span className="font-semibold text-gray-900">
                    {fiscalData?.prenom} {fiscalData?.nom}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>Né(e) le {fiscalData?.dateNaissance || '15/03/1980'}</span>
                </div>
                <div className="flex items-start space-x-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{fiscalData?.adresse}, {fiscalData?.codePostal} {fiscalData?.ville}</span>
                </div>
              </div>
            </div>

            {/* Document Badge */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-[#1e3a5f]" />
                <span className="font-medium text-gray-800">AVIS D'IMPOSITION</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Revenu fiscal:</span>
                  <span className="ml-2 font-medium">{fiscalData?.revenuFiscal} €</span>
                </div>
                <div>
                  <span className="text-gray-500">Parts:</span>
                  <span className="ml-2 font-medium">{fiscalData?.nombreParts}</span>
                </div>
              </div>
            </div>

            {/* Employment & Income Section */}
            {(user?.monthlyIncome || user?.employmentStatus) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Briefcase className="h-5 w-5 text-[#1e3a5f]" />
                  <span className="font-medium text-gray-800">SITUATION PROFESSIONNELLE</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  {user?.employmentStatus && (
                    <div>
                      <span className="text-gray-500">Statut:</span>
                      <span className="ml-2 font-medium">
                        {user.employmentStatus === 'salarie' ? 'Salarié(e)' :
                         user.employmentStatus === 'retraite' ? 'Retraité(e)' :
                         user.employmentStatus === 'independant' ? 'Indépendant' :
                         user.employmentStatus === 'chomeur' ? 'Demandeur d\'emploi' :
                         user.employmentStatus === 'etudiant' ? 'Étudiant(e)' :
                         user.employmentStatus}
                      </span>
                    </div>
                  )}
                  {user?.monthlyIncome && (
                    <div>
                      <span className="text-gray-500">Revenus mensuels:</span>
                      <span className="ml-2 font-medium">{parseFloat(user.monthlyIncome).toLocaleString()} €</span>
                    </div>
                  )}
                </div>
                {user?.employer && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-500">Employeur:</span>
                    <span className="ml-2 font-medium">{user.employer}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Share Button */}
          <Button
            onClick={handleShare}
            className="w-full text-white font-semibold py-4 mb-4"
            style={{ backgroundColor: '#8B5CF6' }}
            disabled={loading}
            data-testid="share-data-btn"
          >
            {loading ? 'Envoi en cours...' : 'Je partage ces informations avec Younited'}
          </Button>

          {/* Retry Link */}
          <button 
            onClick={handleRetry}
            className="w-full flex items-center justify-center space-x-2 text-sm text-[#1e3a5f] hover:underline"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Réessayer avec Impots.gouv.fr</span>
          </button>

          {/* Trust Badges */}
          <div className="flex items-center justify-center space-x-4 pt-6 mt-6 border-t border-gray-200">
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <span className="font-semibold">RGPD</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <span className="font-semibold">DSA</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <span className="font-semibold">DSP2</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareData;
